/**
 * Design Tokens - Brick Builder 3D
 *
 * Centralized design system values following DESIGN_GUIDELINES.md
 * These tokens ensure consistency across all UI components
 *
 * Reference: docs/DESIGN_GUIDELINES.md
 */

export const designTokens = {
  // Color Palette
  colors: {
    primary: {
      pink: '#E91E8C',
      yellow: '#FFD93D',
      blue: '#6BCF9E',
    },
    background: {
      dark: '#1A1A1A',
      light: '#F5F5F5',
    },
    semantic: {
      success: '#6BCF9E',
      warning: '#FFD93D',
      error: '#E91E8C',
      info: '#4A90E2',
    },
    brickColors: {
      red: 'rgb(220, 50, 50)',
      blue: 'rgb(50, 100, 220)',
      green: 'rgb(50, 200, 80)',
      yellow: 'rgb(255, 220, 50)',
      orange: 'rgb(255, 140, 50)',
      purple: 'rgb(150, 50, 200)',
    },
  },

  // Spacing System (8px base unit)
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
  },

  // Typography
  typography: {
    fontFamily: {
      primary: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
      monospace: "'Fira Code', 'Consolas', monospace",
    },
    fontSize: {
      h1: '32px',
      h2: '24px',
      h3: '18px',
      bodyLarge: '16px',
      body: '14px',
      caption: '12px',
    },
    fontWeight: {
      bold: 700,
      semiBold: 600,
      regular: 400,
    },
    lineHeight: {
      tight: 1.2,
      normal: 1.5,
      relaxed: 1.8,
    },
  },

  // Border Radius
  borderRadius: {
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    round: '50%',
  },

  // Shadows
  shadows: {
    sm: '0 2px 4px rgba(0,0,0,0.05)',
    md: '0 2px 8px rgba(0,0,0,0.1)',
    lg: '0 4px 12px rgba(0,0,0,0.15)',
  },

  // Animation
  animation: {
    duration: {
      micro: '100ms',
      fast: '200ms',
      medium: '300ms',
      slow: '500ms',
    },
    easing: {
      easeOut: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      easeInOut: 'cubic-bezier(0.42, 0, 0.58, 1)',
      spring: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    },
  },

  // Component-specific tokens
  components: {
    toolbar: {
      heightMobile: '64px',
      heightDesktop: '80px',
    },
    card: {
      sizeMobile: '80px',
      sizeDesktop: '100px',
      borderWidth: '3px',
    },
    statusBar: {
      height: '40px',
    },
    icon: {
      compact: '20px',
      standard: '24px',
      featured: '32px',
      large: '48px',
    },
    button: {
      height: '48px',
      heightMobile: '56px',
      iconSize: '40px',
    },
  },

  // Breakpoints
  breakpoints: {
    mobile: '640px',
    tablet: '1024px',
  },
}

// Helper function to get card color by index (rotates through primary colors)
export const getCardColor = (index: number): string => {
  const colors = [
    designTokens.colors.primary.pink,
    designTokens.colors.primary.yellow,
    designTokens.colors.primary.blue,
  ]
  return colors[index % colors.length] ?? designTokens.colors.primary.pink
}

// Export type for TypeScript usage
export type DesignTokens = typeof designTokens
